import React, { useEffect, useState, useRef } from 'react';
import { ConfigProvider, Layout } from 'antd';
import { shape, string } from 'prop-types';
import { currentLocale, isTest } from 'helpers/userSessionSettings';
import { OPEN_SERVICE_DESK_REQUEST_EVENT } from 'helpers/serviceDeskEvents';
import {
  sideMenuOptionsPropTypes,
  topMenuOptionsPropTypes,
  issueFormOptionsPropTypes,
  sideMenuOptionsDefaultPropTypes,
  topMenuOptionsDefaultPropTypes,
  issueFormOptionsDefaultPropTypes,
} from './propTypes';
import RightPart from './nested/RightPart';
import LeftPart from './nested/LeftPart';
import SideMenu from './nested/SideMenu';
import Trigger from './nested/Trigger';
import Issue from './nested/Issue';
import useSideMenuItems from './hooks/useSideMenuItems';
import useWidthFlags from './hooks/useWidthFlags';

const { Header } = Layout;
const SIDE_MENU_HOVER_DELAY_MS = 400;

const NavigationMenu = ({
  currentOpenSection = null,
  sideMenuOptions = sideMenuOptionsDefaultPropTypes,
  topMenuOptions = topMenuOptionsDefaultPropTypes,
  selectedKey = null,
  issueFormOptions = issueFormOptionsDefaultPropTypes,
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);

  useEffect(() => {
    const openIssueModal = () => {
      if (issueFormOptions.canCreateTicket) setIsIssueModalOpen(true);
    };
    addEventListener(OPEN_SERVICE_DESK_REQUEST_EVENT, openIssueModal);

    return () => removeEventListener(OPEN_SERVICE_DESK_REQUEST_EVENT, openIssueModal);
  }, [issueFormOptions.canCreateTicket]);

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filterCollapseActiveKey, setFilterCollapseActiveKey] = useState('1');

  const shouldCollapse = (!isHovered && dropdownOpen) ? (!isHovered && !dropdownOpen) : collapsed;

  const sideMenuItems = useSideMenuItems(
    sideMenuOptions,
    collapsed,
    isHovered,
    selectedKey,
    currentOpenSection,
  );

  const {
    hideSideBar,
    showCwoLogoSmall,
    dontShowCwoLogo,
  } = useWidthFlags();

  const hoverTimeoutRef = useRef(null);
  const mouseOverRef = useRef(false);

  const onMouseEnterSider = () => {
    mouseOverRef.current = true;

    hoverTimeoutRef.current = setTimeout(() => {
      if (mouseOverRef.current) {
        setIsHovered(true);
      }
    }, SIDE_MENU_HOVER_DELAY_MS);
  };

  const onMouseLeaveSider = () => {
    mouseOverRef.current = false;

    clearTimeout(hoverTimeoutRef.current);
    setIsHovered(false);
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          motionUnit: isTest ? 0 : 0.1,
        },
        components: {
          Layout: {
            siderBg: '#14151c',
          },
          Menu: {
            darkItemBg: '#14151c',
            darkSubMenuItemBg: '#232630',
            darkItemHoverBg: '#363948',
            darkItemSelectedBg: '#363948',
          },
        },
      }}
    >
      <Header className="navigation-top-menu">
        <LeftPart
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          currentCountry={topMenuOptions.currentCountry}
          showCwoLogoSmall={showCwoLogoSmall}
          dontShowCwoLogo={dontShowCwoLogo}
        />
        <RightPart
          topMenuOptions={topMenuOptions}
          currentLocale={currentLocale}
          filterDrawerOpen={filterDrawerOpen}
          setFilterDrawerOpen={setFilterDrawerOpen}
          filterCollapseActiveKey={filterCollapseActiveKey}
          setFilterCollapseActiveKey={setFilterCollapseActiveKey}
          onOpenIssueModal={() => setIsIssueModalOpen(true)}
        />
      </Header>

      <Issue
        {...issueFormOptions}
        isModalOpen={isIssueModalOpen}
        setIsModalOpen={setIsIssueModalOpen}
      />

      <Layout.Sider
        className="navigation-side-menu-container"
        collapsible
        collapsed={!isHovered && shouldCollapse}
        width={280}
        collapsedWidth={hideSideBar ? 0 : 64}
        onMouseEnter={onMouseEnterSider}
        onMouseLeave={onMouseLeaveSider}
        trigger={null}
      >
        <SideMenu
          selectedKey={selectedKey}
          sideMenuItems={sideMenuItems}
          collapsed={!isHovered && shouldCollapse}
        />
        <Trigger
          triggerOptions={sideMenuOptions.trigger}
          setDropdownOpen={setDropdownOpen}
        />
      </Layout.Sider>
    </ConfigProvider>
  );
};

NavigationMenu.propTypes = {
  sideMenuOptions: shape(sideMenuOptionsPropTypes),
  topMenuOptions: shape(topMenuOptionsPropTypes),
  selectedKey: string,
  currentOpenSection: string,
  issueFormOptions: shape(issueFormOptionsPropTypes),
};

export default NavigationMenu;
